
<script context="module">
  import { __ } from '~/data-utils';

  export function quoteattr(str) {
    return String(str).split('').map(x => {
      const code = x.charCodeAt();
      const isAlpha = code >= 65 && code <= 122;
      const isNum = code >= 48 && code <= 57;
      return isAlpha || isNum ? x :`&#${code}`;
    }).join('');
  }

  class Key {
    constructor (key) {
      const qa = quoteattr(key);
      this.key = key;
      this.className = '';
      this.testid = `key-${qa}`;
      this.title = `Key ${qa}`;
    }

    value () {
      return this.key;
    }

    elKey (mode) {
      return `${mode}_${this.key}`;
    }
  }

  class CommandKey extends Key {
    constructor (key, icon, text = '', isMode) {
      super(key);
      this.icon = icon;

      if (text.startsWith('common.')) {
        this.l10nKey = text;
      }
      else {
        this.text = text;
      }

      this.className = `key-command--${key.toLowerCase()}`;
      this.isMode = isMode;
    }

    value () {
      const { l10nKey, text } = this;
      return l10nKey ? __(this.l10nKey) : text;
    }
  }

  const ABC = 'ABC';
  const DEL = 'DEL';
  const ENTER = 'ENTER';
  const NUM = 'NUM';
  const SHIFT = 'SHIFT';
  const SPACE = 'SPACE';
  const SPECIAL = 'SPECIAL';

  const COMMANDS = {
    ABC: new CommandKey(ABC, null, 'ABC', true),
    DEL: new CommandKey(DEL, 'ic_key-backspace'),
    ENTER: new CommandKey(ENTER, null, 'common.search'),
    NUM: new CommandKey(NUM, null, '123', true),
    SHIFT: new CommandKey(SHIFT, 'ic_key-shift'),
    SPACE: new CommandKey(SPACE, null, 'common.space'),
    SPECIAL: new CommandKey(SPECIAL, null, '#+=', true),
  };

  const getKey = key => new Key(key);
</script>

<script>
  import * as R from 'ramda';
  import { onDestroy, createEventDispatcher } from 'svelte';
  import { slide } from 'svelte/transition';

  import { lang } from 'apps/imd/stores';
  import UseResource from 'components/svg/UseResource.svelte';

  const rawAlpha_standard = [
    'qwertyuiop',
    'asdfghjkl',
    'SHIFT zxcvbnm DEL',
    'NUM SPACE ENTER',
  ];

  const rawAlpha_fi_sv = [
    'qwertyuiopå',
    'asdfghjklöä',
    'SHIFT zxcvbnm DEL',
    'NUM SPACE ENTER',
  ];

  const rawAlpha_da_nb = [
    'qwertyuiopå',
    'asdfghjkløæ',
    'SHIFT zxcvbnm DEL',
    'NUM SPACE ENTER',
  ];

  const rawNums = [
    '1234567890',
    '-/:;()€&@"',
    'SPECIAL .,?!\' DEL',
    'ABC SPACE ENTER'
  ];
  const rawSpecial = [
    '[]{}#%^*+=',
    '_\\|~<>£¥·',
    'NUM .,?!\' DEL',
    'ABC SPACE ENTER'
  ];

  const mapSplit = delim => R.map(R.split(delim));
  const isCommand = /^[A-Z]+$/;

  // toKeys :: [KeyChart] -> [[Key|CommandKey]]
  //  KeyChart = String
  const toKeys = R.pipe(
    mapSplit(' '),
    R.map(
      R.chain(
        R.ifElse(
          R.test(isCommand),
          R.prop(R.__, COMMANDS),
          R.pipe(
            mapSplit(''),
            R.flatten,
            R.map(getKey),
            R.flatten
          )
        )
      )
    )
  );


  let mode = ABC;
  const modes = new Map([
    [ABC, toKeys(
      ['fi', 'sv', 'et'].includes($lang)
        ? rawAlpha_fi_sv
        : ['da', 'nb'].includes($lang)
          ? rawAlpha_da_nb
          : rawAlpha_standard
    )
    ],
    [NUM, toKeys(rawNums)],
    [SPECIAL, toKeys(rawSpecial)]
  ]);

  // Select mode only after there's language selected
  $: keys = $lang ? modes.get(mode) : [];
  let shift = false;

  const dispatch = createEventDispatcher();

  export let value;
  export let input;

  onDestroy(
    value.subscribe(v => {
      if ($input) $input.value = v;
    })
  );

  function keyPress(key) {
    if (!$input) {
      console.warn('No input');
      return;
    }

    let { key: _key } = key;

    if (key.isMode) {
      mode = _key;
    }
    else if (_key == SHIFT) {
      shift = !shift;
    }
    else {
      const {
        value: inputValue,
        selectionStart: start,
        selectionEnd: end
      } = $input;
      let caretAdd = 1;
      let skip;
      let newValue;

      switch (_key) {
      case DEL:
        skip = !inputValue || start + end == 0;
        caretAdd = skip ? 0 : -1;
        newValue = skip ? inputValue : inputValue.substring(0, start - 1) + inputValue.substring(end);
        break;
      case ENTER:
        dispatch('click.enter');
        newValue = inputValue;
        caretAdd = 0;
        break;

      case SPACE: _key = ' ';
      // falls through
      default:
        _key = shift ? _key.toUpperCase() : _key;
        newValue = inputValue.substring(0, start) + _key + inputValue.substring(end);
      }

      // Update input
      $input.value = newValue;
      $input.focus();
      $input.setSelectionRange(
        start + caretAdd,
        end + caretAdd
      );

      value.set(newValue);
    }
  }

  // const slideLeft = (row, key) => 100 * row + 25 * key;
  const slideCenter = (row, key, rows, keys) =>
    150 * row + 25 * Math.abs(keys.length * .5 - key);

</script>

<div class="virtual-keyboard" class:virtual-keyboard--shift={shift}
  data-testid="virtual-keyboard">
  {#each keys as row, ri}
    <div class="key-row">
      {#each row as key, ki (key.elKey(mode))}
        <button class="key {key.className}"
          class:key-command="{key instanceof CommandKey}"
          class:key-command--icon={key.icon}
          on:click="{() => keyPress(key)}"
          in:slide="{{ delay: slideCenter(ri, ki, keys, row), duration: 200 }}"
          data-testid={key.testid}
          title={key.title}>
          {#if key.icon}
            <UseResource name={key.icon} />
          {:else}
            {key.value()}
          {/if}
        </button>
      {/each}
    </div>
  {/each}
</div>


<style media="screen">
  .key-row {
    display: flex;
    justify-content: center;
    height: 80px;
  }

  .key {
    width: 72px;
    height: 72px;
    margin: 4px;
    background: var(--virtualkeyboard--key--bg);
    border: var(--virtualkeyboard--key--border);
    border-radius: var(--virtualkeyboard--key--borderRadius);
    color: var(--virtualkeyboard--key--color);
    font-size: 32px;
    transition: all .15s;
  }

  .key:focus {
    outline: none;
  }

  .key:active {
    transform: scale(1.4);
    z-index: 10;
  }

  .virtual-keyboard--shift .key {
    text-transform: uppercase;
  }

  .key-command {
    width: 100px;
    background: var(--virtualkeyboard--command--bg);
    color: var(--virtualkeyboard--command--color);
    text-transform: none !important;
  }

  .key-command--icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .key-command--icon :global(.resource) {
    filter: var(--virtualkeyboard--key-icon--filter, none);
  }

  .key-command--space {
    width: 400px;
  }

  .key-command--enter {
    background: var(--virtualkeyboard--enter--bg);
    color: var(--virtualkeyboard--enter--color);
  }
</style>
